import React, { useContext, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  ClickAwayListener,
  Fade,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Paper,
  Popper,
  Stack,
  Switch,
  Typography,
  useMediaQuery
} from "@mui/material";
import BottomNavigation from "@mui/material/BottomNavigation";
import BottomNavigationAction from "@mui/material/BottomNavigationAction";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useUnload from "../hooks/useUnload";
import ConfirmationDialog from "./ConfirmationDialog";
import { redirectToLogoutPage } from "../utils/auth-functions";
import {
  downloadFile,
  postBatchConfig,
  uploadFile,
} from "../../data/configAPI";
import { ThemeContext } from "../../contexts/ThemeContext";
import { AuthContext } from "../../contexts/AuthContext";
import { ConfiguratorContext } from "../../contexts/ConfiguratorContext";
import { FlexContainer } from "./styled/Container.styled";
import { CustomNavbar } from "./styled/Nav.styled";
import { PillButton } from "./styled/Button.styled";
import Logo from "./Logo";
import { useTheme } from "@mui/styles";
import AccountPopper from "./AccountPopper";
import ActionSnackbar from "./ActionSnackbar";
import {
  ActiveLoanNavbarLabel,
  LoanDropdownItemLabel,
} from "./LoanMenuLabel";
import {
  DOCUMENTS_ALLOW_ACCESS_KEY,
  DOCUMENTS_ALLOW_REQUEST_PAYOFF_KEY,
  DOCUMENTS_ALLOW_UPLOAD_KEY,
  LOGOUT_HAS_MESSAGE_KEY,
  LOGOUT_MESSAGE_KEY,
  LOGOUT_SUBTITLE_KEY,
  LOGO_IMAGE_KEY,
  FOOTER_COMPANY_NAME_KEY,
  PUBLISH_CHANGES_CONFIRMATION_BODY,
} from "../constants/Configurator.constants";
import {
  CLIENT_ADMIN_ROLE,
  PAYMENTS_BORROWER_ROLE,
  PAYMENTS_SUPPORT_ROLE,
} from "../constants/Roles.constants";
import { callLoansApi, determineRoleLabel } from "../utils/auth-functions";
import { isLoanClosedToPayments } from "../utils/payment-functions";
import {
  collectFailedPublishKeys,
  findEscrowShortageFeeError,
  isLocalMockMode,
} from "../utils/configurator-functions";
import { GENERIC_ERROR_MESSAGE } from "../constants/Defaults.constants";
import {
  blobToBase64,
  findInArray,
  getLoanId,
  hex2rgba,
  isAllowed,
  loansMatch,
} from "../utils/utility-functions";
import {
  saveBrandingColorsToSession,
  saveLogoutLogoToSession,
} from "../utils/brandingSession";
export default function Navbar() {
  const { addPendingRequest, removePendingRequest } = useContext(ThemeContext);
  const {
    edittedFields,
    addBatchOfEdittedFields,
    addNewEdittedField,
    hasValidationErrors,
  } = useContext(ConfiguratorContext);
  const {
    token,
    userDetails,
    userRoles,
    visibleRole,
    setVisibleRole,
    loans,
    setLoans,
    activeLoan,
    setActiveLoan,
    activeLoanDetails,
    impersonatedUser,
    impersonatedUserDetails,
    setImpersonatedUser,
    setImpersonatedUserDetails,
  } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const loanClosedToPayments = isLoanClosedToPayments(
    activeLoanDetails?.loanDetails,
  );
  const [checked, setChecked] = useState(true);
  const [confirmPublishDialogOpen, setConfirmPublishDialogOpen] =
    useState(false);

  const [loansPopperOpen, setLoansPopperOpen] = useState(false);
  const [anchorElement, setAnchorElement] = useState(null);
  const [tabletOrientation, setTabletOrientation] = useState('landscape');
  
  useEffect(() => {
    const detectTabletOrientation = () => {
      if (window.innerWidth >= 768 && window.innerWidth <= 1024) {
        const isLandscape = window.innerWidth > window.innerHeight;
        setTabletOrientation(isLandscape ? 'landscape' : 'portrait');
        
        if (loansPopperOpen) {
          setLoansPopperOpen(false);
          setTimeout(() => {
            setLoansPopperOpen(true);
          }, 100);
        }
      }
    };

    detectTabletOrientation();
    window.addEventListener('resize', detectTabletOrientation);
    window.addEventListener('orientationchange', detectTabletOrientation);

    return () => {
      window.removeEventListener('resize', detectTabletOrientation);
      window.removeEventListener('orientationchange', detectTabletOrientation);
    };
  }, [loansPopperOpen]);

  const [accountPopperOpen, setAccountPopperOpen] = useState(false);
  const [accountAnchorElement, setAccountAnchorElement] = useState(null);


  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState(GENERIC_ERROR_MESSAGE);

  const [selectedLogo, setSelectedLogo] = useState(null);
  const [missingLogo, setMissingLogo] = useState(false);
  const [renderedImage, setRenderedImage] = useState();

  const isBorrower =
    userRoles?.includes(PAYMENTS_BORROWER_ROLE) ||
    userRoles?.includes(CLIENT_ADMIN_ROLE) ||
    impersonatedUser;

  const configuratorDocumentsAllowField = findInArray(
    edittedFields,
    "key",
    DOCUMENTS_ALLOW_ACCESS_KEY,
  );
  const configuratorDocumentsUploadAllowField = findInArray(
    edittedFields,
    "key",
    DOCUMENTS_ALLOW_UPLOAD_KEY,
  );
  const configuratorPayoffQuoteAllowField = findInArray(
    edittedFields,
    "key",
    DOCUMENTS_ALLOW_REQUEST_PAYOFF_KEY,
  );

  const canViewDocuments = isAllowed(
    configuratorDocumentsAllowField,
    "value",
    "true",
    true,
  );
  const canUploadDocuments = isAllowed(
    configuratorDocumentsUploadAllowField,
    "value",
    "true",
    true,
  );
  const canPayoffQuote = isAllowed(
    configuratorPayoffQuoteAllowField,
    "value",
    "true",
    true,
  );

  useEffect(() => {
    const ac = new AbortController();
    const getLogoData = async () => {
      if (token) {
        const currentLogoImage = edittedFields.find(
          (item) => item.key === LOGO_IMAGE_KEY,
        );
        if (currentLogoImage) {
          setSelectedLogo(currentLogoImage.value);
        } else {
          await callImageApi(token, ac.signal);
        }
      }
    };
    void getLogoData();
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, edittedFields]);

  useEffect(() => {
    if (!selectedLogo) {
      setRenderedImage(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedLogo);
    setRenderedImage(objectUrl);


    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedLogo]);

  const callImageApi = async (token, signal) => {
    try {
      const imageData = await downloadFile(
        token,
        LOGO_IMAGE_KEY,
        impersonatedUser,
        signal,
      );
      if (signal.aborted) {
        return;
      }
      if (imageData?.type?.includes("image")) {
        addNewEdittedField({
          key: LOGO_IMAGE_KEY,
          value: imageData,
          modified: false,
        });
        setSelectedLogo(imageData);
        setMissingLogo(false);
      } else {
        setMissingLogo(true);
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        console.error(e);
      }
    }
  };

  useEffect(() => {
    if (userRoles?.includes(CLIENT_ADMIN_ROLE)) {
      if (checked) {
        setVisibleRole(CLIENT_ADMIN_ROLE);
      } else {
        setVisibleRole(PAYMENTS_BORROWER_ROLE);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles, checked]);

  useUnload((e) => {
    if (edittedFields.some((item) => item.modified) || impersonatedUser) {
      e.preventDefault();
      e.returnValue = "";
    }
  });

  const handleChange = (e) => {
    setChecked(e.target.checked);
  };

  const saveLogoutMarketingItems = async () => {
    if (selectedLogo) {
      const base64Logo = await blobToBase64(selectedLogo);
      saveLogoutLogoToSession(base64Logo);
    }
    sessionStorage.setItem(
      "_hm",
      edittedFields.find((item) => item.key === LOGOUT_HAS_MESSAGE_KEY)
        ?.value === "true",
    );
    sessionStorage.setItem(
      "_m",
      edittedFields.find((item) => item.key === LOGOUT_MESSAGE_KEY)?.value ||
      "",
    );
    sessionStorage.setItem(
      "_s",
      edittedFields.find((item) => item.key === LOGOUT_SUBTITLE_KEY)?.value ||
      "",
    );
    saveBrandingColorsToSession(theme.palette);
    sessionStorage.setItem(
      "_cn",
      edittedFields.find((item) => item.key === FOOTER_COMPANY_NAME_KEY)?.value ||
      "Mortgage Builder",
    );
  };

  const logout = async () => {
    setAccountPopperOpen(false);
    addPendingRequest();
    if (edittedFields.some((item) => item.modified)) {
      const confirmationResponse = await window.confirm(
        "Are you sure you want to log out before publishing your changes?",
      );
      if (confirmationResponse) {
        const modifiedElements = edittedFields.map((item) => {
          return { ...item, modified: false };
        });
        addBatchOfEdittedFields(modifiedElements);

        saveLogoutMarketingItems();
        removePendingRequest();
        redirectToLogoutPage();
      } else {
        removePendingRequest();
      }
    } else {
      saveLogoutMarketingItems();
      removePendingRequest();
      redirectToLogoutPage();
    }
  };

  const handlePopperClose = (e, anchorEl, setter) => {
    if (anchorEl.current && anchorEl.current.contains(e.target)) {
      return;
    }

    setter(false);
  };

  const openLoanPopper = (e) => {
    if (loans?.length > 0) {
      setAnchorElement(e.currentTarget);
      setLoansPopperOpen(true);
    }
  };

  // Handle orientation changes and responsive behavior
  useEffect(() => {
    const handleOrientationChange = () => {
      // Close loan popper when orientation changes
      setLoansPopperOpen(false);
      setAnchorElement(null);
      
      // Additional handling for tablets
      if (window.innerWidth >= 768 && window.innerWidth <= 1024) {
        // Tablet detected - ensure popper is properly closed
        setTimeout(() => {
          setLoansPopperOpen(false);
          setAnchorElement(null);
        }, 50);
      }
    };

    const handleResize = () => {
      // Close loan popper when screen size changes
      setLoansPopperOpen(false);
      setAnchorElement(null);
      
      // Handle tablet orientation changes
      if (window.innerWidth >= 768 && window.innerWidth <= 1024) {
        // Tablet detected - ensure popper is properly closed
        setTimeout(() => {
          setLoansPopperOpen(false);
          setAnchorElement(null);
        }, 50);
      }
    };

    // Force refresh of loan list highlighting on orientation change
    const handleTabletOrientationChange = () => {
      if (window.innerWidth >= 768 && window.innerWidth <= 1024) {
        // Force re-render to ensure proper highlighting
        setLoansPopperOpen(false);
        setTimeout(() => {
          if (loans?.length > 0) {
            setLoansPopperOpen(true);
          }
        }, 100);
      }
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleResize);
    
    // Add specific tablet orientation handling
    if (window.innerWidth >= 768 && window.innerWidth <= 1024) {
      window.addEventListener('orientationchange', handleTabletOrientationChange);
    }

    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleTabletOrientationChange);
    };
  }, [loans]);

  const selectActiveLoan = (selectedLoan) => {
    setActiveLoan(getLoanId(selectedLoan));
    setLoansPopperOpen(false);
    // Close popper immediately after selection on all devices
    setAnchorElement(null);
    
    // On mobile devices, ensure popper is closed
    if (isMobile) {
      // Force close on mobile for consistent behavior
      setTimeout(() => {
        setLoansPopperOpen(false);
        setAnchorElement(null);
      }, 100);
    }
  };

  const handleLoanPopperClose = (e) => {
    // Close loan popper when clicking outside or after selection
    if (anchorElement && !anchorElement.current?.contains(e.target)) {
      setLoansPopperOpen(false);
      setAnchorElement(null);
    }
  };

  const toggleAccountPopper = (e) => {
    if (accountPopperOpen) {
      setAccountAnchorElement(null);
      setAccountPopperOpen(false);
      return;
    }
    setAccountAnchorElement(e.currentTarget);
    setAccountPopperOpen(true);
  };

  const accountCallback = () => {
    navigate("/profile");
    setAccountPopperOpen(false);
  };

  const impersonateCallback = () => {
    navigate("/support");
    setAccountPopperOpen(false);
  };

  const endImpersonationSessionCallback = () => {
    setImpersonatedUser(null);
    setImpersonatedUserDetails(null);
    setAccountPopperOpen(false);
    setLoansPopperOpen(false);
    setAnchorElement(null);
    setAccountAnchorElement(null);
    setVisibleRole(userRoles[0]);
    callLoansApi(
      token,
      null,
      setLoans,
      setActiveLoan,
      addPendingRequest,
      removePendingRequest,
      userRoles,
      null,
    );
    if (userRoles?.includes(CLIENT_ADMIN_ROLE)) {
      navigate("/client-configurations", { replace: true });
    } else if (userRoles?.includes(PAYMENTS_SUPPORT_ROLE)) {
      navigate("/support", { replace: true });
    }
  };

  const publishConfiguratorChanges = () => {
    // Refused rather than partly written. The batch write saves each key on its own,
    // so publishing a half-configured pair leaves the database in the broken state
    // and reports success for it.
    const settingsError = findEscrowShortageFeeError(edittedFields);
    if (settingsError) {
      setConfirmPublishDialogOpen(false);
      setSnackbarMessage(settingsError);
      setSnackbarOpen(true);
      return;
    }

    const requestBody = edittedFields
      .filter((item) => item.modified && !item.key.includes("IMAGE"))
      .map((item) => {
        return {
          operation: "POST",
          property: {
            key: item.key,
            value: item.value,
          },
        };
      });

    const fileRequestBody = edittedFields
      .filter((item) => item.modified && item.key.includes("IMAGE"))
      .map((item) => {
        return {
          id: item.key,
          file: item.value,
        };
      });

    callPostConfigApi(token, requestBody, fileRequestBody);
    setConfirmPublishDialogOpen(false);
  };

  const callPostConfigApi = async (token, requestBody, fileRequestBody) => {
    addPendingRequest();
    let failedKeys = new Set();
    const mockMode = isLocalMockMode();

    // Image keys (LOGO_IMAGE, BANNER_IMAGE_*, ADVERT_IMAGE_*) are excluded here and
    // sent via uploadFile only — image-only publish yields requestBody === [] by design.
    if (requestBody.length > 0 && !mockMode) {
      const writeResponse = await postBatchConfig(
        token,
        requestBody,
        impersonatedUser,
      );
      failedKeys = collectFailedPublishKeys(writeResponse, requestBody);
    }

    for (const item of fileRequestBody) {
      const uploadResponse = await uploadFile(token, item, impersonatedUser);
      if (!mockMode && uploadResponse?.success !== true) {
        failedKeys.add(item.id);
      }
    }

    removePendingRequest();
    const modifiedElements = edittedFields.map((item) => {
      return { ...item, modified: failedKeys.has(item.key) };
    });
    addBatchOfEdittedFields(modifiedElements);

    if (failedKeys.size > 0) {
      setSnackbarMessage(
        "Some changes could not be published. Please retry and verify your inputs.",
      );
      setSnackbarOpen(true);
    }
  };
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileNav, setMobileNav] = useState("/");

  useEffect(() => {
    setMobileNav(location.pathname.startsWith("/payments") ? "/payments" :
      location.pathname.startsWith("/statements-documents") ? "/statements-documents" :
      location.pathname.startsWith("/loans") ? "/loans" :
      location.pathname.startsWith("/more") ? "/more" : "/");
  }, [location.pathname]);

  return (
    <>
    {!isMobile && (
    <CustomNavbar position="static">
      <FlexContainer justifycontent="space-between" width="100%">
        <FlexContainer
          justifycontent="flex-start"
          padding="10px 15px"
          flexshrink="1"
          sx={{
            gap: {
              md: '25px',
              lg: '50px'
            }
          }}
        >
          {renderedImage || missingLogo ? (
            <Logo
              src={missingLogo ? "/MB-no-padding-white.png" : renderedImage}
              clickAction={(e) => navigate("/")}
              cursor="pointer"
            />
          ) : (
            <Box width="150px" />
          )}
          {isBorrower ? (
            <>
              <Link
                to="/"
                className={location.pathname === "/" ? "active" : ""}
              >
                Dashboard
              </Link>
              {!loanClosedToPayments ? (
                <Link
                  to="/payments"
                  className={
                    location.pathname.includes("/payments") ? "active" : ""
                  }
                >
                  Payments
                </Link>
              ) : null}
              {canViewDocuments || canUploadDocuments || canPayoffQuote ? (
                <Link
                  to="/statements-documents"
                  className={
                    location.pathname.includes("/statements-documents")
                      ? "active"
                      : ""
                  }
                >
                  {"Statements & Documents"}
                </Link>
              ) : null}
            </>
          ) : null}
          {userRoles?.includes(CLIENT_ADMIN_ROLE) && !impersonatedUser ? (
            <Link
              to="/client-configurations"
              className={
                location.pathname.includes("/client-configurations")
                  ? "active"
                  : ""
              }
            >
              Client Configurations
            </Link>
          ) : null}
          {userRoles?.includes(CLIENT_ADMIN_ROLE) && !impersonatedUser ? (
            <FormGroup>
              <FormControlLabel
                control={<Switch checked={checked} onChange={handleChange} />}
                label={determineRoleLabel(visibleRole)}
              />
            </FormGroup>
          ) : null}
        </FlexContainer>
        <FlexContainer
          background={
            impersonatedUser
              ? theme.palette.info.main
              : theme.palette.secondary.main
          }
          padding="0 10px"
          flexshrink="5"
          alignself="stretch"
          borderradius="0"
          gap="10px"
          sx={{
            color: impersonatedUser
              ? theme.palette.info.contrastText
              : theme.palette.secondary.contrastText,
            "& .MuiTypography-root": { color: "inherit" },
            "& .MuiSvgIcon-root": { color: "inherit" },
            "& .MuiFormControlLabel-label": { color: "inherit" },
          }}
        >
          {userRoles?.includes(CLIENT_ADMIN_ROLE) &&
            edittedFields.some((item) => item.modified) ? (
            <PillButton
              variant="contained"
              color="primary"
              disabled={hasValidationErrors}
              onClick={(e) => setConfirmPublishDialogOpen(true)}
            >
              Publish
            </PillButton>
          ) : (
            <FlexContainer
              gap="5px"
              sx={{
                cursor: loans?.length > 1 ? "pointer" : "default",
                paddingRight: impersonatedUserDetails ? "25px" : 0,
              }}
              onClick={openLoanPopper}
            >
              <Typography>
                {activeLoan === "no-loan" ? (
                  ""
                ) : (
                  <ActiveLoanNavbarLabel
                    activeLoan={activeLoan}
                    loans={loans}
                    activeLoanDetails={activeLoanDetails}
                  />
                )}
              </Typography>
              {loans?.length > 0 ? (
                <>
                  <KeyboardArrowDownIcon
                    sx={{
                      border: "1px solid transparent",
                      borderRadius: "5px",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    }}
                   
                  />
                  <Popper
                    open={loansPopperOpen && Boolean(anchorElement)}
                    anchorEl={anchorElement}
                    placement="bottom-start"
                    transition
                    strategy="fixed"
                    key={`popper-${tabletOrientation}`}
                    sx={{ 
                      zIndex: (theme) => theme.zIndex.modal + 2,
                      '& .MuiPaper-root': {
                        minWidth: '220px',
                        maxWidth: '340px',
                        boxShadow: (theme) => theme.shadows[4]
                      },
                      '@media (min-width: 768px) and (max-width: 1024px)': {
                        '& .MuiPaper-root': {
                          minWidth: '260px',
                          maxWidth: '360px'
                        }
                      }
                    }}
                    modifiers={[
                      {
                        name: 'preventOverflow',
                        options: {
                          boundary: 'viewport',
                          padding: 8,
                        },
                      },
                      {
                        name: 'flip',
                        options: {
                          fallbackPlacements: ['bottom-end', 'top-start', 'top-end'],
                        },
                      },
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 10],
                        },
                      },
                    ]}
                  >
                    {({ TransitionProps }) => (
                      <Fade {...TransitionProps} timeout={350}>
                        <Paper
                          sx={{
                            paddingTop: 1,
                            paddingBottom: 1,
                            marginTop: 1,
                          }}
                        >
                          <ClickAwayListener
                            onClickAway={(e) => {
                              // Close popper when clicking outside on all devices
                              handleLoanPopperClose(e);
                              // Additional check for mobile devices
                              if (isMobile) {
                                setLoansPopperOpen(false);
                                setAnchorElement(null);
                              }
                            }}
                          >
                            <Stack>
                              {loans.map((item) => (
                                <MenuItem
                                  key={getLoanId(item)}
                                  onClick={(e) => selectActiveLoan(item)}
                                  selected={loansMatch(item, activeLoan)}
                                  sx={{
                                    backgroundColor:
                                      loansMatch(item, activeLoan)
                                        ? hex2rgba(
                                            theme.palette.secondary.main,
                                            0.12,
                                          )
                                        : "transparent",
                                    "&:hover": {
                                      backgroundColor: hex2rgba(
                                        theme.palette.secondary.main,
                                        0.08,
                                      ),
                                    },
                                    "&.Mui-selected": {
                                      backgroundColor: hex2rgba(
                                        theme.palette.secondary.main,
                                        0.12,
                                      ),
                                      "&:hover": {
                                        backgroundColor: hex2rgba(
                                          theme.palette.secondary.main,
                                          0.16,
                                        ),
                                      },
                                    },
                                  }}
                                >
                                  <LoanDropdownItemLabel
                                    loan={item}
                                    activeLoan={activeLoan}
                                    activeLoanDetails={activeLoanDetails}
                                  />
                                </MenuItem>
                              ))}
                            </Stack>
                          </ClickAwayListener>
                        </Paper>
                      </Fade>
                    )}
                  </Popper>
                </>
              ) : (
                ""
              )}
            </FlexContainer>
          )}
          {impersonatedUserDetails ? (
            <Avatar
              onClick={toggleAccountPopper}
              sx={{
                backgroundColor: theme.palette.secondary.main,
                border: `1px solid ${theme.palette.secondary.contrastText}`,
                cursor: "pointer",
                position: "absolute",
                right: "35px",
                zIndex: 1000,
              }}
            >
              {impersonatedUserDetails?.displayName?.substring(0, 1) || "U"}
            </Avatar>
          ) : null}
          <Avatar
            onClick={toggleAccountPopper}
            sx={{
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.secondary.contrastText,
              cursor: "pointer",
            }}
          >
            {userDetails?.name?.substring(0, 1) || "U"}
          </Avatar>
          <AccountPopper
            accountPopperOpen={accountPopperOpen}
            anchorElement={accountAnchorElement}
            handleAccountPopperClose={(e) =>
              handlePopperClose(e, accountAnchorElement, setAccountPopperOpen)
            }
            accountCallback={accountCallback}
            settingsCallback={() => { }}
            impersonateCallback={impersonateCallback}
            endImpersonationSessionCallback={endImpersonationSessionCallback}
            logoutCallback={logout}
          />
        </FlexContainer>
      </FlexContainer>
      <ActionSnackbar
        severity="error"
        borderColor={theme.palette.error.main}
        vertical="top"
        horizontal="center"
        width={{ xs: "90%", md: "50%" }}
        isOpen={snackbarOpen}
        closeFn={(e) => {
          setSnackbarOpen(false);
        }}
        snackbarLabel={snackbarMessage}
        hasAction={false}
      />
      <ConfirmationDialog
        open={confirmPublishDialogOpen}
        close={(e) => publishConfiguratorChanges()}
        dismiss={(e) => setConfirmPublishDialogOpen(false)}
        title="Publish changes"
        body={PUBLISH_CHANGES_CONFIRMATION_BODY}
        confirmButtonLabel="Yes"
        dismissButtonLabel="No"
      />
    </CustomNavbar>
    )}

    {isMobile && (
      <Box
        sx={{
          position: "fixed",
          bottom: 10,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1200,
          width: "99%",
        }}
      >
        <BottomNavigation
          sx={{
            bgcolor: theme.palette.primary.dark,
            borderRadius: "20px",
            width: "100%",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          }}
          showLabels
          value={mobileNav}
          onChange={(e, newValue) => {
            setMobileNav(newValue);
            navigate(newValue);
          }}
        >
          <BottomNavigationAction
            label="Dashboard"
            value="/"
            icon={<></>}
            sx={{
              color: theme.palette.common.white,
              bgcolor: mobileNav === "/" ? theme.palette.primary.main : "transparent",
              borderRadius: "16px",
              flex: 1,
              minWidth: 0,
              maxWidth: "none",
              "& .MuiBottomNavigationAction-label": { fontSize: "0.75rem", fontWeight: 500, color: theme.palette.common.white },
            }}
          />
          <BottomNavigationAction
            label="Payments"
            value="/payments"
            icon={<></>}
            sx={{
              color: theme.palette.common.white,
              bgcolor: mobileNav === "/payments" ? theme.palette.primary.main : "transparent",
              borderRadius: "16px",
              flex: 1,
              minWidth: 0,
              maxWidth: "none",
              "& .MuiBottomNavigationAction-label": { fontSize: "0.75rem", fontWeight: 500, color: theme.palette.common.white },
            }}
          />
          <BottomNavigationAction
            label="Statements & Documents"
            value="/statements-documents"
            icon={<></>}
            sx={{
              color: theme.palette.common.white,
              bgcolor: mobileNav === "/statements-documents" ? theme.palette.primary.main : "transparent",
              borderRadius: "16px",
              flex: 1,
              minWidth: 0,
              maxWidth: "none",
              "& .MuiBottomNavigationAction-label": { fontSize: "0.75rem", fontWeight: 500, color: theme.palette.common.white },
            }}
          />
        </BottomNavigation>
      </Box>
    )}
    </>
  );
}